/**
 * **图像控件**：值是一段 `data:` URI（内嵌数据，不联网）。
 *
 * ## 为什么是 `.ts` 而不是 `.vue`
 *
 * 和 `NpPanelCard.ts` 同一条理由：**纯逻辑那套验证脚本用 esbuild 打包，
 * 它不认 `.vue`**。包的 `panels.ts` 要直接复用这个控件（节点上和面板里
 * 必须是同一份实现，否则上传 / 粘贴 / 清除三个动作会漂移），
 * 所以它必须是 `.ts`。
 *
 * ## 为什么值不是"图片地址"
 *
 * 真机反馈两条：
 *   · "把图片做成一个基础类型？比如 base64 编码的文本"；
 *   · "不建议使用 URL，因为这需要解析代码去做 URL 访问功能"。
 *
 * 设计器是离线的（求值三禁令的第一条）：让一个参数值触发网络访问，
 * 等于在"离线可重放"这条承诺上撕个口子。所以图像就是**数据本身**。
 */
import { computed, defineComponent, h, ref, type PropType, type VNode } from 'vue'
import type { ImageWidget } from '../../types/widget'

export const NpWidgetImage = defineComponent({
  name: 'NpWidgetImage',
  props: {
    widget: { type: Object as PropType<ImageWidget>, required: true },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const fileEl = ref<HTMLInputElement | null>(null)
    /** "粘贴 base64"那一栏默认收起：多数人用上传 */
    const pasteOpen = ref(false)
    const pasteText = ref('')

    const value = computed(() => props.widget.value.trim())
    const hasImage = computed(() => value.value.length > 0)
    /** 给人看的体积 —— 内嵌数据会进文档，用户有权知道它多大 */
    const sizeText = computed(() => {
      const bytes = Math.floor((value.value.length * 3) / 4)
      return bytes > 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${bytes} B`
    })

    /**
     * 选中文件 → 读成 data URL。
     *
     * ⚠️ 两样都缺（`FileReader` / 文件输入）时**安静地不生效**：
     * 这套控件也会在无头环境里被渲染（验证脚本读的是结构），
     * 那里没有文件系统，不能因此炸掉。
     */
    function onFile(ev: Event): void {
      const input = ev.target as HTMLInputElement
      const file = input.files?.[0]
      if (!file || typeof FileReader === 'undefined') return
      const reader = new FileReader()
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : ''
        if (text.startsWith('data:')) emit('update', text)
      }
      reader.readAsDataURL(file)
      /* 同一个文件连着选两次也要触发 change */
      input.value = ''
    }

    function applyPaste(): void {
      const text = pasteText.value.trim()
      if (!text) return
      emit('update', text.startsWith('data:') ? text : `data:image/png;base64,${text}`)
      pasteText.value = ''
      pasteOpen.value = false
    }

    return (): VNode => {
      const children: (VNode | null)[] = [
        h('span', { class: ['np-widget__thumb', hasImage.value ? '' : 'is-empty'] }, [
          hasImage.value
            ? h('img', { src: value.value, alt: '' })
            : h('span', { class: 'np-widget__thumb-empty' }, '没有图'),
        ]),
        h(
          'button',
          {
            class: 'np-widget__mini',
            type: 'button',
            'data-np-image': 'upload',
            onClick: () => fileEl.value?.click(),
          },
          '上传',
        ),
        h(
          'button',
          {
            class: 'np-widget__mini',
            type: 'button',
            'data-np-image': 'paste',
            onClick: () => {
              pasteOpen.value = !pasteOpen.value
            },
          },
          '粘贴',
        ),
        hasImage.value
          ? h(
              'button',
              {
                class: 'np-widget__mini',
                type: 'button',
                'data-np-image': 'clear',
                onClick: () => emit('update', ''),
              },
              '清除',
            )
          : null,
        hasImage.value ? h('span', { class: 'np-widget__size' }, sizeText.value) : null,
        h('input', {
          ref: fileEl,
          class: 'np-widget__file',
          type: 'file',
          accept: 'image/*',
          'data-np-image': 'file',
          onChange: onFile,
        }),
        pasteOpen.value
          ? h('span', { class: 'np-widget__paste' }, [
              h('textarea', {
                class: 'np-widget__field',
                rows: 2,
                placeholder: '粘贴 base64 或 data: URI',
                value: pasteText.value,
                onInput: (ev: Event) => {
                  pasteText.value = (ev.target as HTMLTextAreaElement).value
                },
              }),
              h(
                'button',
                { class: 'np-widget__mini', type: 'button', onClick: applyPaste },
                '确定',
              ),
            ])
          : null,
      ]
      return h(
        'span',
        { class: 'np-widget np-widget--image nodrag', title: props.widget.hint },
        children,
      )
    }
  },
})
